import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Button, Card, Nav, Navbar, Badge, Accordion, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faRocket, faRobot, faShoppingCart, faComments, faCode, 
  faMobileAlt, faServer, faShieldAlt, faChartLine, faCheck,
  faEnvelope, faDollarSign, faLightbulb, faHeadset, faPuzzlePiece,
  faSearch, faTags, faBox, faShoppingBag, faUsers, faArrowRight,
  faBell, faStar, faUser, faBarcode
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faTelegram, faShopify, faWordpress } from '@fortawesome/free-brands-svg-icons';

// Import CSS specific to landing page
import '../styles/LandingPage.css';
import api from '../utils/api';

function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeDemo, setActiveDemo] = useState('whatsapp');
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState('');
  const integrationRef = useRef(null);
  
  // Simulated chat messages for the interactive demo
  const demoMessages = {
    whatsapp: [
      { sender: 'user', text: "Hi, do you have the Nike Air Max in size 10?" },
      { sender: 'bot', text: "Hello! Yes, we have the Nike Air Max available in size 10. There are currently 3 in stock. Would you like to see the colors available?" },
      { sender: 'user', text: "Yes please. And what's the price?" },
      { sender: 'bot', text: "We have Black, White, and Red colors in stock. The price is $129.99. I can send you a secure payment link if you'd like to make a purchase." }
    ],
    telegram: [
      { sender: 'user', text: "I'd like to check the status of my order #78543" },
      { sender: 'bot', text: "Let me check that for you. Order #78543 was shipped yesterday via FedEx. The tracking number is FDX7823456912. It's expected to be delivered in 2 days." },
      { sender: 'user', text: "Great, thanks! Can I change the delivery address?" },
      { sender: 'bot', text: "I'm afraid the package is already in transit, so we can't change the delivery address. However, you can contact FedEx directly with your tracking number to arrange for pickup at a different location." }
    ]
  };

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Animation effect for integration icons
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const icons = integrationRef.current.querySelectorAll('.integration-icon');
          icons.forEach((icon, index) => {
            setTimeout(() => {
              icon.classList.add('animate-in');
            }, index * 200);
          });
        }
      },
      { threshold: 0.2 }
    );
    
    if (integrationRef.current) {
      observer.observe(integrationRef.current);
    }
    
    return () => {
      if (integrationRef.current) {
        observer.unobserve(integrationRef.current);
      }
    };
  }, []);

  // Smooth scroll functionality
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    api.get('/plans')
      .then(res => setPlans(res.data.plans || []))
      .catch(() => setPlansError('Failed to load plans'))
      .finally(() => setPlansLoading(false));
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <Navbar 
        expand="lg" 
        fixed="top" 
        className={`landing-navbar ${scrolled ? 'scrolled' : ''}`}
      >
        <Container>
          <Navbar.Brand href="#home" className="d-flex align-items-center">
            <div className="brand-logo">
              <FontAwesomeIcon icon={faRobot} className="brand-icon" />
              <span className="brand-text">AI Chat Connect</span>
            </div>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto">
              <Nav.Link onClick={() => scrollToSection('features')}>Features</Nav.Link>
              <Nav.Link onClick={() => scrollToSection('platforms')}>Platforms</Nav.Link>
              <Nav.Link onClick={() => scrollToSection('how-it-works')}>How It Works</Nav.Link>
              <Nav.Link onClick={() => scrollToSection('pricing')}>Pricing</Nav.Link>
              <Nav.Link as={Link} to="/login" className="ms-lg-2">Login</Nav.Link>
              <Button as={Link} to="/register" className="ms-lg-2 signup-btn">Try Free</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-particles"></div>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <Badge bg="light" text="dark" className="new-badge mb-3 animate__animated animate__fadeInDown">
                <FontAwesomeIcon icon={faRocket} className="me-1" /> New: Voice Message Support
              </Badge>
              <h1 className="hero-title animate__animated animate__fadeInUp">
                AI-Powered E-commerce Support on Your Customers' Favorite Chat Apps
              </h1>
              <p className="hero-subtitle animate__animated animate__fadeInUp animate__delay-1s">
                Transform customer engagement with AI that connects your store to WhatsApp and Telegram. Provide instant answers about products, orders, and shipping.
              </p>
              <div className="hero-buttons animate__animated animate__fadeInUp animate__delay-2s">
                <Button variant="primary" size="lg" as={Link} to="/register" className="primary-btn me-3">
                  Start Free Trial
                </Button>
                <Button variant="outline-light" size="lg" onClick={() => scrollToSection('how-it-works')} className="secondary-btn">
                  View Demo
                </Button>
              </div>
              
              <div className="platform-badges mt-4 animate__animated animate__fadeInUp animate__delay-3s">
                <div className="d-flex align-items-center">
                  <div className="me-3">Works with:</div>
                  <div className="platforms-container">
                    <div className="platform whatsapp">
                      <FontAwesomeIcon icon={faWhatsapp} />
                    </div>
                    <div className="platform telegram">
                      <FontAwesomeIcon icon={faTelegram} />
                    </div>
                    <div className="platform shopify">
                      <FontAwesomeIcon icon={faShopify} />
                    </div>
                    <div className="platform woo">
                      <FontAwesomeIcon icon={faWordpress} />
                    </div>
                    <div className="platform odoo">
                      <img src="https://odoocdn.com/openerp_website/static/img/assets/png/odoo_logo.png" alt="Odoo" />
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="chat-demo-container animate__animated animate__fadeInRight">
                <div className="chat-demo-header">
                  <div className="chat-demo-tabs">
                    <button 
                      className={`tab-btn ${activeDemo === 'whatsapp' ? 'active' : ''}`} 
                      onClick={() => setActiveDemo('whatsapp')}
                    >
                      <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Demo
                    </button>
                    <button 
                      className={`tab-btn ${activeDemo === 'telegram' ? 'active' : ''}`} 
                      onClick={() => setActiveDemo('telegram')}
                    >
                      <FontAwesomeIcon icon={faTelegram} /> Telegram Demo
                    </button>
                  </div>
                </div>
                <div className="chat-demo-body">
                  {demoMessages[activeDemo].map((msg, index) => (
                    <div key={index} className={`chat-bubble ${msg.sender}`}>
                      {msg.text}
                      <div className="chat-time">
                        {msg.sender === 'bot' && (
                          <Badge bg={activeDemo === 'whatsapp' ? 'success' : 'primary'} className="ai-badge">
                            <FontAwesomeIcon icon={faRobot} className="me-1" /> AI
                          </Badge>
                        )}
                        {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="chat-demo-footer">
                  <div className="demo-input-group">
                    <input type="text" placeholder="Type a message..." className="demo-input" />
                    <button className="demo-send-btn">
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <div className="integrations-bar animate__animated animate__fadeInUp animate__delay-4s">
            <div className="trusted-by">Trusted by 1000+ stores including:</div>
            <div className="logos-container">
              <img src="https://cdn.shopify.com/shopifycloud/brochure/assets/logo-soup/bajaao-small-6ca0459c2a2fc6a57654d85cd1f053ac5e02c1d3ce7b51672bba0d9d628d84c6.png" alt="Brand Logo" />
              <img src="https://cdn.shopify.com/shopifycloud/brochure/assets/logo-soup/johnjacobs-small-79aa379de8a1c0d6df1c33af8c63a677871164315ccbc495423aeaead650d53a.png" alt="Brand Logo" />
              <img src="https://cdn.shopify.com/shopifycloud/brochure/assets/logo-soup/yogabar-small-4d90804c9156272a7237302928cea9c55361f28fdb3ef32a3c0246f0b886cc33.png" alt="Brand Logo" />
              <img src="https://cdn.shopify.com/shopifycloud/brochure/assets/logo-soup/starstruck-small-6d49ff528f3ef3217015f906a1ece473e2559688669d00e2b88e7be4f65ce3bf.png" alt="Brand Logo" />
              <img src="https://cdn.shopify.com/shopifycloud/brochure/assets/logo-soup/mcaffeine-small-0c55170838a60e6a490dbe9cad225b5be7b8b9916130b7a0c44187662dcca1f7.png" alt="Brand Logo" />
            </div>
          </div>
        </Container>
        <div className="hero-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path fill="#ffffff" fillOpacity="1" d="M0,128L80,144C160,160,320,192,480,186.7C640,181,800,139,960,128C1120,117,1280,139,1360,149.3L1440,160L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"></path>
          </svg>
        </div>
      </section>
      
      {/* Integration Platforms Section */}
      <section id="platforms" className="platforms-section" ref={integrationRef}>
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Seamless Platform Integration</h2>
            <p className="section-subtitle">Connect your favorite e-commerce platform with popular messaging channels</p>
          </div>
          
          <Row className="justify-content-center text-center">
            <Col md={4} className="mb-5">
              <div className="integration-category">
                <h3 className="category-title">E-commerce Platforms</h3>
                <div className="integration-icons">
                  <div className="integration-icon shopify">
                    <FontAwesomeIcon icon={faShopify} />
                    <span>Shopify</span>
                  </div>
                  <div className="integration-icon woocommerce">
                    <FontAwesomeIcon icon={faWordpress} />
                    <span>WooCommerce</span>
                  </div>
                  <div className="integration-icon odoo">
                    <img src="https://odoocdn.com/openerp_website/static/img/assets/png/odoo_logo.png" alt="Odoo" />
                    <span>Odoo</span>
                  </div>
                </div>
              </div>
            </Col>
            
            <Col md={4} className="mb-5">
              <div className="integration-diagram">
                <div className="diagram-arrow">
                  <FontAwesomeIcon icon={faArrowRight} size="2x" />
                </div>
                <div className="diagram-center">
                  <div className="circle-pulse"></div>
                  <FontAwesomeIcon icon={faRobot} className="diagram-icon" />
                  <span>AI Engine</span>
                </div>
                <div className="diagram-arrow">
                  <FontAwesomeIcon icon={faArrowRight} size="2x" />
                </div>
              </div>
            </Col>
            
            <Col md={4} className="mb-5">
              <div className="integration-category">
                <h3 className="category-title">Messaging Channels</h3>
                <div className="integration-icons">
                  <div className="integration-icon whatsapp">
                    <FontAwesomeIcon icon={faWhatsapp} />
                    <span>WhatsApp</span>
                  </div>
                  <div className="integration-icon telegram">
                    <FontAwesomeIcon icon={faTelegram} />
                    <span>Telegram</span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          
          <div className="platform-features">
            <Row>
              <Col md={6} lg={4} className="mb-4">
                <Card className="feature-card ecommerce-card">
                  <Card.Body>
                    <FontAwesomeIcon icon={faShopify} className="platform-icon shopify-icon" />
                    <h4>Shopify Integration</h4>
                    <ul className="platform-feature-list">
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Automatic product sync
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Real-time inventory updates
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Order status notifications
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Customer profile access
                      </li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6} lg={4} className="mb-4">
                <Card className="feature-card ecommerce-card">
                  <Card.Body>
                    <FontAwesomeIcon icon={faWordpress} className="platform-icon woo-icon" />
                    <h4>WooCommerce Integration</h4>
                    <ul className="platform-feature-list">
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        WordPress plugin support
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Product catalog access
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Order management
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Customer data integration
                      </li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6} lg={4} className="mb-4">
                <Card className="feature-card ecommerce-card">
                  <Card.Body>
                    <div className="platform-icon odoo-icon">
                      <img src="https://odoocdn.com/openerp_website/static/img/assets/png/odoo_logo.png" alt="Odoo" />
                    </div>
                    <h4>Odoo Integration</h4>
                    <ul className="platform-feature-list">
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        ERP system connection
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Product & inventory sync
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Sales order automation
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Customer communication
                      </li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6} lg={6} className="mb-4">
                <Card className="feature-card messaging-card whatsapp-card">
                  <Card.Body>
                    <FontAwesomeIcon icon={faWhatsapp} className="platform-icon" />
                    <h4>WhatsApp Business API</h4>
                    <ul className="platform-feature-list">
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Official Business API integration
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Green verification checkmark
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        24-hour service window management
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Template message support
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Rich media messages (images, buttons)
                      </li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={6} lg={6} className="mb-4">
                <Card className="feature-card messaging-card telegram-card">
                  <Card.Body>
                    <FontAwesomeIcon icon={faTelegram} className="platform-icon" />
                    <h4>Telegram Bot API</h4>
                    <ul className="platform-feature-list">
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Custom Telegram bot creation
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Inline keyboard buttons
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Media file sharing
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        Group chat support
                      </li>
                      <li>
                        <FontAwesomeIcon icon={faCheck} className="me-2" />
                        URL & deep linking capabilities
                      </li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">AI-Powered E-commerce Features</h2>
            <p className="section-subtitle">Transform your customer service with intelligent automated responses</p>
          </div>

          <Row className="g-4">
            <Col lg={4} md={6}>
              <Card className="feature-card h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="feature-icon product-icon">
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                  <Card.Title>Product Search & Discovery</Card.Title>
                  <Card.Text>
                    Let customers find products naturally by asking in their own words. AI understands product attributes and customer intent.
                  </Card.Text>
                  <div className="feature-example mt-3">
                    <div className="chat-example">
                      <div className="example-message user">
                        "Do you have waterproof hiking boots under $100?"
                      </div>
                      <div className="example-message bot">
                        "Yes! I found 3 waterproof hiking boots under $100. The most popular is our TrailMaster X4, currently on sale for $89.99."
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} md={6}>
              <Card className="feature-card h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="feature-icon order-icon">
                    <FontAwesomeIcon icon={faBox} />
                  </div>
                  <Card.Title>Order Tracking & Updates</Card.Title>
                  <Card.Text>
                    Let customers track orders, get shipping updates, and receive notifications about delivery status automatically.
                  </Card.Text>
                  <div className="feature-example mt-3">
                    <div className="chat-example">
                      <div className="example-message user">
                        "What's the status of my order #45872?"
                      </div>
                      <div className="example-message bot">
                        "Your order #45872 was shipped yesterday via FedEx. It's currently in transit and expected to arrive by Wednesday, May 15."
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} md={6}>
              <Card className="feature-card h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="feature-icon inventory-icon">
                    <FontAwesomeIcon icon={faBarcode} />
                  </div>
                  <Card.Title>Real-time Inventory Checking</Card.Title>
                  <Card.Text>
                    Inform customers about product availability, variants in stock, and estimated restock dates for sold-out items.
                  </Card.Text>
                  <div className="feature-example mt-3">
                    <div className="chat-example">
                      <div className="example-message user">
                        "Is the iPhone 13 Pro available in blue?"
                      </div>
                      <div className="example-message bot">
                        "We have the iPhone 13 Pro in Sierra Blue with 128GB and 256GB in stock. The 512GB version is currently out of stock but expected to be restocked next week."
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} md={6}>
              <Card className="feature-card h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="feature-icon recommendation-icon">
                    <FontAwesomeIcon icon={faTags} />
                  </div>
                  <Card.Title>Product Recommendations</Card.Title>
                  <Card.Text>
                    AI suggests relevant products based on customer inquiries, purchase history, and browsing behavior.
                  </Card.Text>
                  <div className="feature-example mt-3">
                    <div className="chat-example">
                      <div className="example-message user">
                        "I need a gift for my mom who loves gardening"
                      </div>
                      <div className="example-message bot">
                        "Based on our best-selling garden gifts, I'd recommend our Deluxe Garden Tool Set ($49.99) or the Self-Watering Herb Garden Kit ($35.50). Both have excellent reviews from gardening enthusiasts!"
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} md={6}>
              <Card className="feature-card h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="feature-icon support-icon">
                    <FontAwesomeIcon icon={faHeadset} />
                  </div>
                  <Card.Title>Customer Service Automation</Card.Title>
                  <Card.Text>
                    Handle common support questions, return requests, and troubleshooting with AI that knows your policies.
                  </Card.Text>
                  <div className="feature-example mt-3">
                    <div className="chat-example">
                      <div className="example-message user">
                        "How do I return my purchase?"
                      </div>
                      <div className="example-message bot">
                        "Returns are free within 30 days of delivery. To start a return, go to your order history in your account or use the return label in your package. Would you like me to send you our step-by-step return instructions?"
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4} md={6}>
              <Card className="feature-card h-100">
                <Card.Body className="d-flex flex-column">
                  <div className="feature-icon checkout-icon">
                    <FontAwesomeIcon icon={faShoppingBag} />
                  </div>
                  <Card.Title>Conversational Checkout</Card.Title>
                  <Card.Text>
                    Enable customers to complete purchases directly within WhatsApp or Telegram with secure payment links.
                  </Card.Text>
                  <div className="feature-example mt-3">
                    <div className="chat-example">
                      <div className="example-message user">
                        "I want to buy the red dress in size medium"
                      </div>
                      <div className="example-message bot">
                        "Great choice! I've added the Red Summer Dress in size M to your cart. The total is $59.99. Would you like to checkout now? I can send you a secure payment link."
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="how-it-works-section">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">Setting up your AI assistant is quick and simple</p>
          </div>

          <div className="steps-container">
            <Row className="align-items-center step-row">
              <Col md={5} className="mb-4 mb-md-0">
                <div className="step-image-container shopify-setup">
                  <div className="platform-badge">
                    <FontAwesomeIcon icon={faShopify} size="2x" />
                  </div>
                  <img 
                    src="https://cdn.shopify.com/shopifycloud/brochure/assets/home/desktop-refresh/dashboard-floating-cd50aaf10039f11525e6878301887a3a25d2e0e11156cb5f3fa64150a50f7925.jpg" 
                    alt="Connect Store" 
                    className="img-fluid rounded shadow-lg step-image" 
                  />
                </div>
              </Col>
              <Col md={7}>
                <div className="step-content">
                  <div className="step-number">1</div>
                  <h3>Connect Your Store</h3>
                  <p>Select your e-commerce platform and connect with API credentials. We securely sync your product database, orders, and customer information.</p>
                  <ul className="step-features">
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Automated catalog synchronization</li>
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Secure access management</li>
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Real-time inventory updates</li>
                  </ul>
                </div>
              </Col>
            </Row>

            <Row className="align-items-center step-row">
              <Col md={7}>
                <div className="step-content">
                  <div className="step-number">2</div>
                  <h3>Set Up Messaging Channels</h3>
                  <p>Connect WhatsApp Business API and Telegram Bot channels to start receiving customer inquiries directly to your new AI assistant.</p>
                  <ul className="step-features">
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Official WhatsApp Business API integration</li>
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Custom Telegram bot creation</li>
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Automated webhook configuration</li>
                  </ul>
                </div>
              </Col>
              <Col md={5}>
                <div className="step-image-container whatsapp-setup">
                  <div className="platform-badge">
                    <FontAwesomeIcon icon={faWhatsapp} size="2x" />
                  </div>
                  <img 
                    src="https://www.appsflyer.com/wp-content/uploads/2021/10/Banner-image-Mobile-Template-Messages-2-1-1024x538.png" 
                    alt="Configure Channels" 
                    className="img-fluid rounded shadow-lg step-image" 
                  />
                </div>
              </Col>
            </Row>

            <Row className="align-items-center step-row">
              <Col md={5} className="mb-4 mb-md-0">
                <div className="step-image-container ai-setup">
                  <div className="platform-badge">
                    <FontAwesomeIcon icon={faRobot} size="2x" />
                  </div>
                  <img 
                    src="https://assets-global.website-files.com/60e8702601005df8e9d7cc76/65a8532e106948733fb53e9c_social%201.png" 
                    alt="Customize AI" 
                    className="img-fluid rounded shadow-lg step-image" 
                  />
                </div>
              </Col>
              <Col md={7}>
                <div className="step-content">
                  <div className="step-number">3</div>
                  <h3>Customize Your AI Assistant</h3>
                  <p>Train your AI with your specific products, customize its tone, and set up automated responses to match your brand voice.</p>
                  <ul className="step-features">
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Product knowledge customization</li>
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Brand voice tuning</li>
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Response template creation</li>
                  </ul>
                </div>
              </Col>
            </Row>

            <Row className="align-items-center step-row">
              <Col md={7}>
                <div className="step-content">
                  <div className="step-number">4</div>
                  <h3>Launch and Monitor</h3>
                  <p>Go live with your AI assistant and monitor performance through your dashboard. Step in manually when needed for complex inquiries.</p>
                  <ul className="step-features">
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Real-time analytics dashboard</li>
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Manual takeover capability</li>
                    <li><FontAwesomeIcon icon={faCheck} className="me-2" /> Continuous AI improvement</li>
                  </ul>
                </div>
              </Col>
              <Col md={5}>
                <div className="step-image-container dashboard-view">
                  <div className="platform-badge">
                    <FontAwesomeIcon icon={faChartLine} size="2x" />
                  </div>
                  <img 
                    src="https://assets.openai.com/openai.com/2023-11-06-clarity/dalle3-builder-2x.png" 
                    alt="Go Live" 
                    className="img-fluid rounded shadow-lg step-image" 
                  />
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="pricing-bg-pattern"></div>
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="section-subtitle">Choose the plan that fits your business needs</p>
          </div>
          {plansLoading ? (
            <div className="text-center">Loading plans...</div>
          ) : plansError ? (
            <div className="alert alert-danger text-center">{plansError}</div>
          ) : (
            <Row className="justify-content-center pricing-row">
              {plans.map((plan, idx) => {
                let features = plan.features;
                if (typeof features === 'string') {
                  try { features = JSON.parse(features); } catch { features = plan.features; }
                }
                return (
                  <Col lg={4} md={6} className="pricing-col" key={plan.id}>
                    <Card className="pricing-card h-100">
                      <Card.Body className="d-flex flex-column">
                        <div className="pricing-header">
                          <h5 className="pricing-title">{plan.name}</h5>
                          <div className="price-container">
                            <span className="currency">$</span>
                            <span className="price">{plan.price}</span>
                            <span className="period">/{plan.duration} days</span>
                          </div>
                        </div>
                        <p className="plan-description">
                          {Array.isArray(features) ? features.join(', ') : features}
                        </p>
                        <hr className="pricing-divider" />
                        <ul className="pricing-features">
                          {Array.isArray(features)
                            ? features.map((f, i) => <li key={i}><FontAwesomeIcon icon={faCheck} className="check-icon me-2" />{f}</li>)
                            : null}
                        </ul>
                        <div className="mt-auto text-center">
                          <Button variant="primary" size="lg" className="pricing-btn" as={Link} to={`/register?plan_id=${plan.id}`}>
                            {features && typeof features === 'object' && features.free_trial_days ? 'Start Free Trial' : 'Select Plan'}
                          </Button>
                          {features && typeof features === 'object' && features.free_trial_days && (
                            <small className="d-block mt-2">{features.free_trial_days}-day trial, no credit card required</small>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Container>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">What Our Customers Say</h2>
            <p className="section-subtitle">Real results from real businesses using our platform</p>
          </div>

          <Row className="justify-content-center">
            <Col md={4} className="mb-4">
              <Card className="testimonial-card h-100">
                <Card.Body>
                  <div className="testimonial-rating">
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                  </div>
                  <div className="testimonial-text">
                    "Since implementing WhatsApp AI support, we've seen a 35% reduction in customer service costs while increasing customer satisfaction scores by 28%. The integration with Shopify was extremely smooth."
                  </div>
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      <img src="https://randomuser.me/api/portraits/women/45.jpg" alt="Sarah Johnson" />
                    </div>
                    <div className="author-info">
                      <div className="author-name">Sarah Johnson</div>
                      <div className="author-title">E-commerce Director, FashionBoutique</div>
                      <div className="author-platform">
                        <FontAwesomeIcon icon={faShopify} className="me-1" /> Shopify Store
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="testimonial-card h-100">
                <Card.Body>
                  <div className="testimonial-rating">
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                  </div>
                  <div className="testimonial-text">
                    "We integrated our WooCommerce store with the AI assistant on both WhatsApp and Telegram. Now our customers can check order status and find products 24/7. Sales have increased 45% from chat channels alone."
                  </div>
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Michael Rodriguez" />
                    </div>
                    <div className="author-info">
                      <div className="author-name">Michael Rodriguez</div>
                      <div className="author-title">Owner, TechGadgetsHub</div>
                      <div className="author-platform">
                        <FontAwesomeIcon icon={faWordpress} className="me-1" /> WooCommerce Store
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="testimonial-card h-100">
                <Card.Body>
                  <div className="testimonial-rating">
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="star" />
                    <FontAwesomeIcon icon={faStar} className="half-star" />
                  </div>
                  <div className="testimonial-text">
                    "The integration with our Odoo system was seamless. Now our inventory, orders, and customer data are all synced with the AI assistant. Our response time decreased from 5 hours to instant, and conversion rates are up 22%."
                  </div>
                  <div className="testimonial-author">
                    <div className="author-avatar">
                      <img src="https://randomuser.me/api/portraits/women/65.jpg" alt="Emily Chen" />
                    </div>
                    <div className="author-info">
                      <div className="author-name">Emily Chen</div>
                      <div className="author-title">COO, HomeStylePlus</div>
                      <div className="author-platform">
                        <img src="https://odoocdn.com/openerp_website/static/img/assets/png/odoo_logo_small.png" alt="Odoo" className="me-1" style={{width: "16px"}} /> Odoo ERP
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <Container>
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Get answers to common questions about our platform</p>
          </div>

          <Row className="justify-content-center">
            <Col lg={8}>
              <Accordion defaultActiveKey="0" className="faq-accordion">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <FontAwesomeIcon icon={faShoppingCart} className="me-3" />
                    How does the AI integration work with my store?
                  </Accordion.Header>
                  <Accordion.Body>
                    <p>Our platform connects to your e-commerce store through secure API connections. The AI assistant uses this connection to access product information, inventory levels, order statuses, and other data needed to answer customer questions accurately.</p>
                    <div className="faq-platforms">
                      <span className="platform-badge"><FontAwesomeIcon icon={faShopify} /> Shopify</span>
                      <span className="platform-badge"><FontAwesomeIcon icon={faWordpress} /> WooCommerce</span>
                      <span className="platform-badge"><img src="https://odoocdn.com/openerp_website/static/img/assets/png/odoo_logo_small.png" alt="Odoo" className="me-1" style={{width: "16px"}} /> Odoo</span>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <FontAwesomeIcon icon={faCode} className="me-3" />
                    Do I need technical knowledge to set this up?
                  </Accordion.Header>
                  <Accordion.Body>
                    No technical knowledge is required. Our platform provides step-by-step guidance for connecting your store and messaging channels. For WhatsApp Business API and Telegram Bot setup, we provide detailed instructions with screenshots. Most customers can complete the entire setup in less than 30 minutes.
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    <FontAwesomeIcon icon={faComments} className="me-3" />
                    Can I use both WhatsApp and Telegram simultaneously?
                  </Accordion.Header>
                  <Accordion.Body>
                    <p>Yes, our Business and Enterprise plans support multiple channels simultaneously. Your AI assistant will maintain consistent responses across all platforms, and you can view and manage all conversations from one unified dashboard.</p>
                    <div className="faq-platforms">
                      <span className="platform-badge"><FontAwesomeIcon icon={faWhatsapp} /> WhatsApp</span>
                      <span className="platform-badge"><FontAwesomeIcon icon={faTelegram} /> Telegram</span>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="3">
                  <Accordion.Header>
                    <FontAwesomeIcon icon={faUser} className="me-3" />
                    What happens when the AI can't answer a question?
                  </Accordion.Header>
                  <Accordion.Body>
                    When a question falls outside the AI's capabilities or knowledge base, the system notifies you and your team for manual intervention. You can seamlessly take over the conversation through our dashboard. The AI also learns from these interactions to improve future responses.
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="4">
                  <Accordion.Header>
                    <FontAwesomeIcon icon={faDollarSign} className="me-3" />
                    Is there a free trial available?
                  </Accordion.Header>
                  <Accordion.Body>
                    Yes, we offer a 14-day free trial on all plans with full functionality. No credit card is required to start. This allows you to test the platform with your specific setup before committing to a subscription.
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="5">
                  <Accordion.Header>
                    <FontAwesomeIcon icon={faShieldAlt} className="me-3" />
                    How secure is my store data?
                  </Accordion.Header>
                  <Accordion.Body>
                    Security is our top priority. We use industry-standard encryption for all data transfer and storage. Our platform operates on a zero-knowledge principle, meaning we only access the minimum data required for the AI to function. We comply with GDPR and other regional data protection regulations.
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-particles"></div>
        <Container>
          <Row className="justify-content-center">
            <Col md={10} lg={8}>
              <div className="cta-content text-center">
                <h2>Ready to Transform Your Customer Experience?</h2>
                <p className="mb-4">Start your free 14-day trial today. No credit card required.</p>
                <div className="cta-buttons">
                  <Button variant="light" size="lg" as={Link} to="/register" className="me-3 cta-primary-btn">
                    Start Free Trial
                  </Button>
                  <Button variant="outline-light" size="lg" href="#contact" className="cta-secondary-btn">
                    Schedule a Demo
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Form */}
      <section id="contact" className="contact-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6}>
              <div className="section-header text-center mb-5">
                <h2 className="section-title">Contact Us</h2>
                <p className="section-subtitle">Have questions? Get in touch with our team</p>
              </div>
              
              <Card className="contact-card">
                <Card.Body>
                  <Form>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name</Form.Label>
                          <Form.Control type="text" placeholder="Enter your first name" />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control type="text" placeholder="Enter your last name" />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control type="email" placeholder="Enter your email" />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Company</Form.Label>
                      <Form.Control type="text" placeholder="Enter your company name" />
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Platform</Form.Label>
                      <Form.Select>
                        <option>Select your e-commerce platform</option>
                        <option value="shopify">Shopify</option>
                        <option value="woocommerce">WooCommerce</option>
                        <option value="odoo">Odoo</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Message</Form.Label>
                      <Form.Control as="textarea" rows={4} placeholder="Your message..." />
                    </Form.Group>
                    
                    <Button variant="primary" type="submit" className="w-100 contact-btn">
                      Send Message
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="footer-section">
        <Container>
          <Row className="g-4">
            <Col lg={4} md={6} className="mb-4 mb-md-0">
              <div className="footer-logo mb-3">
                <FontAwesomeIcon icon={faRobot} className="me-2" />
                <span className="fw-bold">AI Chat Connect</span>
              </div>
              <p className="mb-3">
                Transforming e-commerce customer support with AI-powered multichannel messaging solutions.
              </p>
              <div className="social-links d-flex gap-2">
                <a href="#" className="social-link facebook">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href="#" className="social-link twitter">
                  <i className="fab fa-twitter"></i>
                </a>
                <a href="#" className="social-link linkedin">
                  <i className="fab fa-linkedin-in"></i>
                </a>
                <a href="#" className="social-link instagram">
                  <i className="fab fa-instagram"></i>
                </a>
              </div>
            </Col>

            <Col lg={2} md={6} className="mb-4 mb-md-0">
              <h5 className="footer-heading">Product</h5>
              <ul className="footer-links">
                <li><a href="#features">Features</a></li>
                <li><a href="#how-it-works">How It Works</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </Col>

            <Col lg={2} md={6} className="mb-4 mb-md-0">
              <h5 className="footer-heading">Platforms</h5>
              <ul className="footer-links">
                <li><a href="#"><FontAwesomeIcon icon={faShopify} /> Shopify</a></li>
                <li><a href="#"><FontAwesomeIcon icon={faWordpress} /> WooCommerce</a></li>
                <li><a href="#">Odoo</a></li>
                <li><a href="#"><FontAwesomeIcon icon={faWhatsapp} /> WhatsApp</a></li>
                <li><a href="#"><FontAwesomeIcon icon={faTelegram} /> Telegram</a></li>
              </ul>
            </Col>

            <Col lg={2} md={6} className="mb-4 mb-md-0">
              <h5 className="footer-heading">Company</h5>
              <ul className="footer-links">
                <li><a href="#">About Us</a></li>
                <li><a href="#">Blog</a></li>
                <li><a href="#">Careers</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </Col>

            <Col lg={2} md={6}>
              <h5 className="footer-heading">Legal</h5>
              <ul className="footer-links">
                <li><a href="#">Privacy Policy</a></li>
                <li><a href="#">Terms of Service</a></li>
                <li><a href="#">Security</a></li>
              </ul>
            </Col>
          </Row>

          <hr className="footer-divider" />

          <div className="footer-bottom">
            <p className="mb-0 text-center">
              &copy; {new Date().getFullYear()} AI Chat Connect. All rights reserved.
            </p>
          </div>
        </Container>
      </footer>
    </div>
  );
}

export default LandingPage;
